import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Collapse,
    Divider,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, usePermissions } from 'react-admin';
import {
    LibraryItem,
    LibraryAccessGrant,
    LibraryAccessType,
    Company,
    libraryService,
} from '../../services/libraryService';

interface OperatorSharingPanelProps {
    open: boolean;
    onClose: () => void;
    item: LibraryItem;
    onUpdated?: () => void;
}

interface CompanyAccess {
    company: Company;
    accessType: LibraryAccessType;
    expiresAt: string;
    isGranted: boolean;
    existingGrant?: LibraryAccessGrant;
}

const OperatorSharingPanel: React.FC<OperatorSharingPanelProps> = ({
    open,
    onClose,
    item,
    onUpdated,
}) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [accessGrants, setAccessGrants] = useState<LibraryAccessGrant[]>([]);
    const [companyAccess, setCompanyAccess] = useState<Record<number, CompanyAccess>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedCompany, setExpandedCompany] = useState<number | null>(null);

    const translate = useTranslate();
    const notify = useNotify();
    const { permissions } = usePermissions();

    const isOperator = Array.isArray(permissions) && permissions.includes('ROLE_OPERATOR');

    useEffect(() => {
        if (open && isOperator) {
            loadData();
        }
    }, [open, item.id, isOperator]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [companiesData, grantsData] = await Promise.all([
                libraryService.getCompanies(),
                libraryService.getLibraryItemAccess(item.id),
            ]);

            setCompanies(companiesData);
            setAccessGrants(grantsData);

            // Build access map
            const accessMap: Record<number, CompanyAccess> = {};
            companiesData.forEach((company) => {
                const existingGrant = grantsData.find((g) => g.companyId === company.id);
                accessMap[company.id] = {
                    company,
                    accessType: existingGrant?.accessType || 'readonly',
                    expiresAt: existingGrant?.expiresAt || '',
                    isGranted: !!existingGrant,
                    existingGrant,
                };
            });
            setCompanyAccess(accessMap);
        } catch (error) {
            notify(translate('library.sharing.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleCompany = (companyId: number) => {
        setCompanyAccess((prev) => ({
            ...prev,
            [companyId]: {
                ...prev[companyId],
                isGranted: !prev[companyId].isGranted,
            },
        }));
    };

    const handleAccessTypeChange = (companyId: number, accessType: LibraryAccessType) => {
        setCompanyAccess((prev) => ({
            ...prev,
            [companyId]: {
                ...prev[companyId],
                accessType,
            },
        }));
    };

    const handleExpiresAtChange = (companyId: number, expiresAt: string) => {
        setCompanyAccess((prev) => ({
            ...prev,
            [companyId]: {
                ...prev[companyId],
                expiresAt,
            },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Process changes
            const promises: Promise<any>[] = [];

            Object.values(companyAccess).forEach((access) => {
                if (access.isGranted) {
                    // Grant or update access
                    promises.push(
                        libraryService.shareLibraryItem(item.id, {
                            companyId: access.company.id,
                            accessType: access.accessType,
                            expiresAt: access.expiresAt || undefined,
                        })
                    );
                } else if (access.existingGrant) {
                    // Revoke access
                    promises.push(
                        libraryService.revokeLibraryAccess(item.id, access.company.id)
                    );
                }
            });

            await Promise.all(promises);
            notify(translate('library.sharing.saveSuccess'), { type: 'success' });
            onUpdated?.();
            onClose();
        } catch (error) {
            notify(translate('library.sharing.saveError'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleRevokeAccess = async (companyId: number) => {
        try {
            await libraryService.revokeLibraryAccess(item.id, companyId);
            notify(translate('library.sharing.revokeSuccess'), { type: 'success' });
            loadData();
        } catch (error) {
            notify(translate('library.sharing.revokeError'), { type: 'error' });
        }
    };

    if (!isOperator) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {translate('library.sharing.title')}
                <Typography variant="body2" color="text.secondary">
                    {item.title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : companies.length === 0 ? (
                    <Alert severity="info">
                        {translate('library.sharing.noCompanies')}
                    </Alert>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate('library.sharing.description')}
                        </Alert>

                        <List>
                            {companies.map((company) => {
                                const access = companyAccess[company.id];
                                const isExpanded = expandedCompany === company.id;

                                return (
                                    <React.Fragment key={company.id}>
                                        <ListItem
                                            sx={{
                                                bgcolor: access?.isGranted ? 'action.selected' : 'transparent',
                                                borderRadius: 1,
                                                mb: 0.5,
                                            }}
                                        >
                                            <Checkbox
                                                checked={access?.isGranted || false}
                                                onChange={() => handleToggleCompany(company.id)}
                                            />
                                            <ListItemText
                                                primary={company.name}
                                                secondary={
                                                    access?.existingGrant ? (
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                            <Chip
                                                                size="small"
                                                                label={
                                                                    access.existingGrant.accessType === 'copy_allowed'
                                                                        ? translate('library.access.copyAllowed')
                                                                        : translate('library.access.referenceOnly')
                                                                }
                                                                color={access.existingGrant.accessType === 'copy_allowed' ? 'success' : 'default'}
                                                            />
                                                            {access.existingGrant.expiresAt && (
                                                                <Chip
                                                                    size="small"
                                                                    label={`${translate('library.sharing.expiresAt')}: ${new Date(access.existingGrant.expiresAt).toLocaleDateString()}`}
                                                                    color={access.existingGrant.isExpired ? 'error' : 'default'}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : null
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                {access?.existingGrant && (
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRevokeAccess(company.id)}
                                                        title={translate('library.sharing.revoke')}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => setExpandedCompany(isExpanded ? null : company.id)}
                                                >
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>

                                        <Collapse in={isExpanded}>
                                            <Box sx={{ pl: 7, pr: 2, pb: 2, display: 'flex', gap: 2 }}>
                                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                                    <InputLabel>
                                                        {translate('library.sharing.accessType')}
                                                    </InputLabel>
                                                    <Select
                                                        value={access?.accessType || 'readonly'}
                                                        label={translate('library.sharing.accessType')}
                                                        onChange={(e) =>
                                                            handleAccessTypeChange(
                                                                company.id,
                                                                e.target.value as LibraryAccessType
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value="readonly">
                                                            {translate('library.sharing.readonly')}
                                                        </MenuItem>
                                                        <MenuItem value="copy_allowed">
                                                            {translate('library.sharing.copyAllowed')}
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>

                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    label={translate('library.sharing.expiresAt')}
                                                    value={access?.expiresAt?.split('T')[0] || ''}
                                                    onChange={(e) =>
                                                        handleExpiresAtChange(company.id, e.target.value)
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    helperText={translate('library.sharing.expiresAtHelp')}
                                                />
                                            </Box>
                                        </Collapse>
                                        <Divider />
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || loading}
                >
                    {saving ? <CircularProgress size={24} /> : translate('ra.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OperatorSharingPanel;
